import React, { useState, useEffect, useMemo, useRef } from 'react';
import TagSelect from '../TagSelect/TagSelect';
import { Guid } from '@Shared.Angular/@types/guid';
import TagGroupApiService from '@Shared.Angular/flowingly.services/taggroup.api.service';
import { useService } from '@Shared.Angular/flowingly.hooks/useService';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';

interface Props {
  usedTagIds: Guid[];
  initialSelectedTagIds?: Guid[];
  onApply: (selectedTags: Guid[]) => void;
  tagLabel?: string;
  popoverAlign?: 'left' | 'right';
}

const TagFilter: React.FC<Props> = ({
  usedTagIds,
  initialSelectedTagIds = [],
  onApply,
  tagLabel = 'Find Tags',
  popoverAlign = 'right'
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Guid[]>(
    initialSelectedTagIds
  );
  const [tempSelectedTags, setTempSelectedTags] = useState<Guid[]>([]);
  const [allTagGroups, setAllTagGroups] = useState<ITagGroupDetail[]>([]);
  const [usedTagGroups, setUsedTagGroups] = useState<ITagGroupDetail[]>([]);
  const triggerRef = useRef<HTMLDivElement>(null);

  const { enableTagging } = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');
  const tagGroupApiService =
    useService<TagGroupApiService>('tagGroupApiService');

  const usedTagsSet = useMemo(() => new Set(usedTagIds), [usedTagIds]);
  
  useEffect(() => {
    setSelectedTags(getValidTags(initialSelectedTagIds, usedTagsSet));
  }, []);

  useEffect(() => {
    tagGroupApiService.getAll().then((tagGroups) => {
      setAllTagGroups(tagGroups);
    });
  }, []);

  useEffect(() => {
    const filteredTagGroups = allTagGroups
      .map((group) => ({
        ...group,
        tags: group.tags.filter((tag) => usedTagsSet.has(tag.id))
      }))
      .filter((group) => group.tags.length > 0);

    setUsedTagGroups(filteredTagGroups);
    resetSelectedTags();
  }, [allTagGroups, usedTagsSet]);

  useEffect(() => {
    const validSelectedTags = getValidTags(selectedTags, usedTagsSet);
    if (validSelectedTags.length !== selectedTags.length) {
      setSelectedTags(validSelectedTags);
      onApply(validSelectedTags);
    }
  }, [usedTagIds]);

  const resetSelectedTags = () => {
    if (usedTagsSet.size === 0) {
      setSelectedTags([]);
    }
  };

  const handleClick = () => {
    setPopoverOpen(true);
    setTempSelectedTags(getValidTags(selectedTags, usedTagsSet));
  };

  const getValidTags = (tags: Guid[], validTagSet: Set<Guid>): Guid[] =>
    tags.filter((tagId) => validTagSet.has(tagId));

  const handleClose = () => {
    setPopoverOpen(false);
  };

  const handleApply = () => {
    setSelectedTags(tempSelectedTags);
    onApply(tempSelectedTags);
    setPopoverOpen(false);
  };

  const handlePopoverClickOutside = (e: MouseEvent) => {
    if (
      triggerRef.current &&
      !triggerRef.current.contains(e.target as Node) &&
      !document.getElementById('tag-filter-popover')?.contains(e.target as Node)
    ) {
      setPopoverOpen(false);
    }
  };

  useEffect(() => {
    popoverOpen
      ? document.addEventListener('mousedown', handlePopoverClickOutside)
      : document.removeEventListener('mousedown', handlePopoverClickOutside);
    return () => {
      document.removeEventListener('mousedown', handlePopoverClickOutside);
    };
  }, [popoverOpen]);

  const count = selectedTags.length;
  const label = count > 0 ? 'Selected' : tagLabel;

  if (!enableTagging) return null;

  return (
    <div className="tag-filter-wrapper">
      <div
        ref={triggerRef}
        className="tag-filter-trigger"
        onClick={handleClick}
      >
        {count > 0 && <span className="tag-count">{count}</span>}
        <span className="tag-label">{label}</span>
        <i className="fa fa-chevron-down tag-icon" />
      </div>

      {popoverOpen && (
        <div
          id="tag-filter-popover"
          className={`tag-filter-popover-container ${
            popoverAlign === 'right' ? 'align-right' : 'align-left'
          }`}
        >
          <TagSelect
            allTagGroups={usedTagGroups}
            selectedTags={tempSelectedTags}
            onChange={(newTags: Guid[]) => setTempSelectedTags(newTags)}
          />

          <div className="tag-filter-actions">
            <button className="btn cancel-button" onClick={handleClose}>
              Cancel
            </button>
            <button className="btn apply-button" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagFilter;
